import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    TextField,
    Alert,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useAppUpdate, useStateSelector } from '../../context/StateSelectors';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import i18n from '../../i18n';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';

function toLocalIsoMinute(value) {
    if (!value) return '';
    const d = typeof value?.toDate === 'function' ? value.toDate() : value;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}:00`;
}
export default function LockInstanceDialog({ open, server, onExit }) {
    const { t } = useTranslation();
    const merchantTerminals = useStateSelector(
        (state) => state.controllersData.publicMerchantTerminals || []
    );
    const update = useAppUpdate();

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [selectedTerminal, setSelectedTerminal] = useState(null);
    const [touched, setTouched] = useState({
        start: false,
        end: false,
        terminal: false
    });

    // const defaultStart = useMemo(
    //     () => moment().minutes(0).seconds(0).milliseconds(0),
    //     []
    // );
    // const defaultEnd = useMemo(
    //     () => moment().add(2, 'hours').minutes(0).seconds(0).milliseconds(0),
    //     []
    // );

    const [start, setStart] = useState(null);
    const [end, setEnd] = useState(null);

    useEffect(() => {
        if (open) {
            setSelectedTerminal(null);
            setError(null);
            setSaving(false);
            setStart(null); // or defaultStart
            setEnd(null); // or defaultEnd
            setTouched({
                terminal: false,
                start: false,
                end: false,
            });
        }
    }, [open]); // add defaultStart and defaultEnd to deps


    const startError =
        touched.start && !start ? t('errors.fieldRequired') : null;

    const endError =
        touched.end && !end
            ? t('errors.fieldRequired')
            : touched.end && start && end && end.isSameOrBefore(start)
                ? t('errors.endDateAfterStart')
                : null;

    const terminalError =
        touched.terminal && !selectedTerminal ? t('errors.fieldRequired') : null;

    const hasErrors = !!(startError || endError || terminalError);
    const emptyFields = !start || !end || !selectedTerminal;

    const handleSave = async () => {
        if (hasErrors || !selectedTerminal || !start || !end) return;
        setSaving(true);
        setError(null);
        try {
            const body = {
                merchantTerminalId: selectedTerminal.id,
                startDate: toLocalIsoMinute(start),
                endDate: toLocalIsoMinute(end)
            };
            const res = await server.lock(body);
            update({
                alertError: {
                    message: res.data,
                    type: 'snackbar',
                    severity: 'success'
                }
            });
            onExit();
        } catch (e) {
            setError(e?.message || t('errors.unknown'));
        } finally {
            setSaving(false);
        }
    };

    const locale = i18n.language === 'en' ? 'en-gb' : i18n.language;


    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                // Prevent accidental close while saving or by backdrop/escape
                if (saving) return;
                if (reason === 'backdropClick' || reason === 'escapeKeyDown')
                    return;
                onExit();
            }}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>{t('text.lockInstanceDialog')}</DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}

                    <Autocomplete
                        options={merchantTerminals}
                        value={selectedTerminal}
                        onChange={(_, v) => {
                            // eslint-disable-next-line no-shadow
                            setTouched((touched) => ({ ...touched, terminal: true }));
                            setSelectedTerminal(v);
                        }}
                        getOptionLabel={(o) =>
                            `${o.name} (UUID: ${o.merchantTerminalUuid})`
                        }
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderOption={(props, option) => {
                            const { key, ...rest } = props;
                            return (
                                <li key={key} {...rest}>
                                    {option.name} (UUID:{' '}
                                    {option.merchantTerminalUuid})
                                </li>
                            );
                        }}
                        renderInput={({ key, ...params }) => (
                            <TextField
                                {...params}
                                label={t('columns.merchantTerminal')}
                                error={!!terminalError}
                                helperText={
                                    terminalError || t('text.selectTerminalToLock')
                                }
                            />
                        )}
                    />

                    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                        <Stack direction="row" spacing={2}>
                            <DateTimePicker
                                label={t('columns.startDate')}
                                value={start}
                                onChange={(v) => {
                                    // eslint-disable-next-line no-shadow
                                    setTouched((touched) => ({ ...touched, start: true }));
                                    setStart(v);
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!startError,
                                        helperText: startError
                                    }
                                }}
                            />
                            <DateTimePicker
                                label={t('columns.endDate')}
                                value={end}
                                onChange={(v) => {
                                    // eslint-disable-next-line no-shadow
                                    setTouched((touched) => ({ ...touched, end: true }));
                                    setEnd(v);
                                }}
                                minDateTime={start || undefined}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!endError,
                                        helperText: endError
                                    }
                                }}
                            />
                        </Stack>
                    </LocalizationProvider>

                </Stack>
            </DialogContent>

            <DialogActions>
                <Button color="inherit" onClick={onExit} disabled={saving}>
                    {t('buttons.cancel')}
                </Button>
                <LoadingButton
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || hasErrors || emptyFields}
                    loading={saving}
                >
                    {saving ? t('buttons.saving') : t('buttons.save')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
